"use client"

import type React from "react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import { Eye, EyeOff } from "lucide-react"
import { useAuthStore } from "../store/authStore"
import { derivePublicKeyFromPrivate, signMessage, generateKeyPairFromPassword } from "../crypto/crypto"
import { env } from "../config/env"
import { toast } from "sonner"

export const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)
  const storedPrivateKey = useAuthStore((state) => state.privateKey)
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const handleLogin = async (e: React.FormEvent) => {
    const API_URL = env.API_URL
    e.preventDefault()
    setError("")
    setLoading(true)

    try {
      let privateKey = storedPrivateKey
      let publicKeyX: string
      let publicKeyY: string

      if (!privateKey) {
        // Private key not in local storage so regenerate from password
        const keyPair = await generateKeyPairFromPassword(password, username)
        privateKey = keyPair.privateKey
        publicKeyX = keyPair.publicKeyX
        publicKeyY = keyPair.publicKeyY
      } else {
        // Derive public key from stored private key
        const derivedKeys = derivePublicKeyFromPrivate(privateKey)
        publicKeyX = derivedKeys.publicKeyX
        publicKeyY = derivedKeys.publicKeyY
      }

      // Request challenge
      const challengeResponse = await axios.post(`${API_URL}/auth/challenge`, {
        username,
      })

      const nonce = challengeResponse.data.data.nonce

      // Sign the nonce
      const signature = signMessage(nonce, privateKey)

      // Send signature and public key to server for verification
      const loginResponse = await axios.post(`${API_URL}/auth/login`, {
        username,
        nonce,
        signature,
        publicKeyX,
        publicKeyY,
      })

      const { token, userId } = loginResponse.data.data
      console.log(`Token: ${token} for user ${userId}`)

      // Store auth data
      setAuth(token, username, userId, privateKey, publicKeyX, publicKeyY)

      navigate("/chat")
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      const errorMessage = error.response?.data?.error || "Login failed"
      setError(errorMessage || "Login failed")
      toast.error("Failed to login", {
        description: `Error when logging in: ${errorMessage}`,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-slate-900 to-slate-800 px-4 py-8">
      <div className="w-full max-w-md bg-slate-800 rounded-lg shadow-xl p-6 sm:p-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 text-center">MeeChat</h1>
        <p className="text-sm sm:text-base text-slate-400 text-center mb-6 sm:mb-8">
          Secure end-to-end encrypted messaging
        </p>

        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-3 rounded mb-4">{error}</div>
        )}

        <form onSubmit={handleLogin} className="space-y-3 sm:space-y-4">
          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="username"
              className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3 sm:px-4 py-2 pr-10 text-sm sm:text-base bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300 cursor-pointer"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white font-semibold py-2 rounded transition cursor-pointer"
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>

        <p className="text-slate-400 text-center mt-6">
          Don't have an account?{" "}
          <a href="/register" className="text-blue-400 hover:text-blue-300">
            Register
          </a>
        </p>
      </div>
    </div>
  )
}
